import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../../../connection/socket";
import { addDocuments, refreshDocumentThumbnail } from "../../../store/actions/admin/content-actions";
import { EventsState } from "../../../store/types";
import { GetDefaultColorPack } from "../../../store/utils/create-event";
import Icon, { COLORS, ICONS } from "../../general-ui/icon";
import LargeButton from "../../general-ui/button/large-button";
import ModalComponent from "../../general-ui/modal/modal";
import NavigationDropdown from "../../general-ui/dropdown/navigation-dropdown";
import WaitingIndicator from "../../general-ui/waiting-indicator/waiting-indicator";
import { ColorEditModal } from "../create/settings/design/colors/color";
import { FontUploadModal } from "../create/settings/design/fonts/fonts";
import './upload-menu.scss';
import { createUploadJob, getChannelUpVideos } from "../../../store/actions/admin/videos";

const UPLOAD_TYPES = {
	DOCUMENT: 'Documents',
	IMAGE: "Images",
	VIDEO: 'Videos',
	FONT: "Fonts",
	PALETTE: "Palettes"
};
const THUMBNAIL_UPDATE_KEY = 'document-thumbnail-ready';
export default function UploadMenu(): JSX.Element {
	const {
		AuthReducer: {
			user,
			token
		},
		ContentReducer: {
			isUploading
		},
	} = useSelector((state: EventsState) => state);
	const [uploadType, setUploadType] = useState("");
	const [files, setFiles] = useState<FileList | File>();
	const dispatch = useDispatch();
	const socket = useSocket(`admin-${user!.active_channel!}`);
	const [uploadingVideo, setUploadingVideo] = useState(false);
	const mounted = useRef(false);

	useEffect(() => {
		const updateThumbnail = (message: { document: number, image: string }) => {
			dispatch(refreshDocumentThumbnail(message));
		};
		socket.addListener(THUMBNAIL_UPDATE_KEY, updateThumbnail);

		return () => socket.removeListener(THUMBNAIL_UPDATE_KEY, updateThumbnail);
	}, [socket, dispatch]);


	const handleCancel = useCallback(() => {
		setFiles(undefined);
		setUploadType("");
	}, []);

	const handleUpload = () => {
		if (!uploadType || !files) { return; }
		if (uploadType === UPLOAD_TYPES.VIDEO) {
			//handle for video
		} else {
			//handle for Docs and Images
			const toUpload = (files instanceof File) ? Array(files) : Object.values(files); //turns File | FileList into iterables
			dispatch(addDocuments(toUpload, user!, token!));
		}
		setFiles(undefined);
		setUploadType("");
	};
	const subtitle = () => {
		switch (uploadType) {
			case (UPLOAD_TYPES.DOCUMENT):
				return "Up to 50 MB file size.";
			case (UPLOAD_TYPES.IMAGE):
				return "Up to 50 MB file size.";
			case (UPLOAD_TYPES.VIDEO):
				return "Up to 500 MB file size.";
			default: return "";
		}
	};

	function handleVideoUpload() {
		dispatch(createUploadJob(() => {
			if (mounted.current) {
				dispatch(getChannelUpVideos(token!, user!.active_channel));
			}
		}, () => {
			setUploadingVideo(true);
		}));
	}

	useEffect(() => {
		mounted.current = true;

		return () => {
			mounted.current = false;
		};
	}, []);

	const getUploadModal = () => {
		switch (uploadType) {
			case (UPLOAD_TYPES.FONT): {
				return (
					<FontUploadModal
						isOpen={!!uploadType}
						onClose={handleCancel}
						user={user!}
					/>);
			}
			case (UPLOAD_TYPES.PALETTE): {
				return (
					<div className="colors-container">
						<ColorEditModal
							modalTitle="Create Color Palette"
							isOpen={!!uploadType}
							onClose={handleCancel}
							token={token!}
							color={GetDefaultColorPack()}
						/>
					</div>
				);
			}
			case (UPLOAD_TYPES.VIDEO): {
				return (
					<ModalComponent
						title={`Upload ${uploadType}`}
						onRequestClose={handleCancel}
						open={!!uploadType && uploadType !== UPLOAD_TYPES.FONT}
						closeable={false}
						cancellable
					>
						{uploadingVideo
							? (
								<div style={{ textAlign: 'center' }}>
									<h3>Video Uploading</h3>
									<p>Your video will continue to upload unless you leave the page.</p>

									<LargeButton
										title={`Upload Another Video`}
										subtitle={"You can upload a video up to 5GB in size."}
										onClick={handleVideoUpload}
									/>

								</div>
							)
							: <LargeButton
								title={`Upload Video`}
								subtitle={"You can upload a video up to 5GB in size."}
								onClick={handleVideoUpload}
							/>
						}
						<div className="add-modal-buttons">
							{/* do we need to do more here to cancel the upload of the video? */}
							<button onClick={handleCancel}>Cancel</button>
							<button className="primary" onClick={handleCancel}>Done</button>
						</div>
					</ModalComponent>
				);
			}
			default: {
				return (
					<ModalComponent
						title={`Upload ${uploadType}`}
						onRequestClose={handleCancel}
						open={!!uploadType && uploadType !== UPLOAD_TYPES.FONT}
						closeable={false}
						cancellable
					>
						{isUploading
							? <div className="waiting-indicator-container"><WaitingIndicator /></div>
							: <LargeButton
								title={`Upload ${uploadType}`}
								subtitle={subtitle()}
								multiple={true}
								onFile={(files) => setFiles(files)}
							/>
						}
						<div className="add-modal-buttons">
							<button onClick={handleCancel}>Cancel</button>
							<button className="primary" onClick={handleUpload} disabled={!files}>Add</button>
						</div>
					</ModalComponent>
				);
			}
		}
	};
	return (
		<div className="upload-menu">
			<NavigationDropdown title="Add" className="add" buttonClassName="small primary">
				<button className="upload-menu-btn clear sharp btn-left-align" onClick={() => setUploadType(UPLOAD_TYPES.DOCUMENT)}><Icon name={ICONS.DOCUMENT} color={COLORS.GRAY} size={14} /> <span>{UPLOAD_TYPES.DOCUMENT}</span></button>
				<button className="upload-menu-btn clear sharp btn-left-align" onClick={() => setUploadType(UPLOAD_TYPES.IMAGE)}><Icon name={ICONS.WALLPAPER} color={COLORS.GRAY} size={14} /> <span>{UPLOAD_TYPES.IMAGE}</span></button>
				<button className="upload-menu-btn clear sharp btn-left-align" onClick={() => setUploadType(UPLOAD_TYPES.VIDEO)}><Icon name={ICONS.CAMERA_ON} color={COLORS.GRAY} size={14} /> <span>{UPLOAD_TYPES.VIDEO}</span></button>
				<button className="upload-menu-btn clear sharp btn-left-align" onClick={() => setUploadType(UPLOAD_TYPES.FONT)}><Icon name={ICONS.FONTS} color={COLORS.GRAY} size={14} /> <span>{UPLOAD_TYPES.FONT}</span></button>
				<button className="upload-menu-btn clear sharp btn-left-align" onClick={() => setUploadType(UPLOAD_TYPES.PALETTE)}><Icon name={ICONS.PALETTES} color={COLORS.GRAY} size={14} /> <span>{UPLOAD_TYPES.PALETTE}</span></button>
			</NavigationDropdown>

			{getUploadModal()}
		</div>
	);
}